import type { ReactNode } from "react";

import type { PaymentState, PaymentInfo } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AlertTriangle, LinkForward } from "@/components/icons";
import { useT, type Translate } from "@/app/localization";
import { moneyMajor, viewerInstant } from "@/app/displayFormat";

// ── Shared payment-status presentation ─────────────────────────────────────────
// The SINGLE source of truth for how a session's payment state is labelled and
// rendered, so every surface (the admin org sessions report AND the per-charger
// Session History) uses IDENTICAL vocabulary. Values are PROJECTIONS of the
// backend's factual payment_state (organizations/session_report.classify_payment)
// — nothing here decides payment.

type BadgeVariant = "success" | "neutral" | "warning" | "error" | "info";

// The payment-facts contract (PaymentInfo) is defined in lib/api.ts — the API
// model owns the backend response shape; this presentation module only renders it.
// OrgSessionReportRow is structurally assignable to PaymentInfo, and the
// per-charger sessions API attaches the same shape as `session.payment`.

// Maps the backend factual payment_state to a badge variant; the label is the
// catalog message billing.payment_state.label selected by the same code. Labels are
// deliberately factual: `Authorized` and `Not captured` are shown only where that
// is what the persisted state actually is; a bare authorization hold with an unknown
// final bill reads as "Billing not finalized", never "not captured".
export const PAYMENT_STATE_META: Record<PaymentState, { variant: BadgeVariant }> = {
  paid: { variant: "success" },
  free: { variant: "neutral" },
  billing_processing: { variant: "info" },
  billing_stuck: { variant: "error" },
  captured_amount_unverified: { variant: "warning" },
  authorized_awaiting_capture: { variant: "warning" },
  capture_failed: { variant: "error" },
  partially_captured: { variant: "error" },
  customer_action_required: { variant: "warning" },
  payment_locked: { variant: "error" },
  canceled: { variant: "neutral" },
  abandoned: { variant: "warning" },
  billing_not_finalized: { variant: "warning" },
  billing_error: { variant: "error" },
  missing_payment_intent: { variant: "error" },
  data_mismatch: { variant: "error" },
  // Migrated (Energic) sessions: billed under a real tariff but nothing captured.
  // "Uncollected" = revenue never collected (with the root cause); "Free · charging
  // schedule" = intentionally waived under a Free Charging Schedule. Terminal, not actionable.
  uncollected_no_payment_method: { variant: "warning" },
  uncollected_charger_offline: { variant: "warning" },
  uncollected_payment_failed: { variant: "warning" },
  uncollected_authorized_not_captured: { variant: "warning" },
  free_charging_schedule: { variant: "neutral" },
  not_authorized: { variant: "neutral" },
  unknown: { variant: "neutral" },
};

export function paymentStateMeta(state: PaymentState): { variant: BadgeVariant } {
  return PAYMENT_STATE_META[state] ?? PAYMENT_STATE_META.unknown;
}

/** Display label of a payment_state code; an unrecognized code reads "Unknown", as before. */
export function paymentStateLabel(t: Translate, state: PaymentState): string {
  return t("billing.payment_state.label", { state });
}

/** Human-readable attention_reason (the machine value stays in the CSV / API); an
 *  unrecognized reason is shown as its raw code. */
export function attentionReasonLabel(t: Translate, reason: string): string {
  return t("billing.attention_reason.label", { reason, raw: reason });
}

function money(v: number | null | undefined): string {
  // Payment amounts are USD by the projection contract (session_report._usd).
  return moneyMajor(v, "USD") ?? "—";
}

// Build the Stripe dashboard deep-link for a payment intent (mode-agnostic — the
// operator can switch test/live in Stripe). Only rendered when an intent exists.
function stripePaymentUrl(intentId: string): string {
  return `https://dashboard.stripe.com/payments/${encodeURIComponent(intentId)}`;
}

/** The status badge on its own (no tooltip) — for compact / non-table surfaces. */
export function PaymentBadge({
  state,
  needsAttention = false,
  className,
}: {
  state: PaymentState;
  needsAttention?: boolean;
  className?: string;
}) {
  const t = useT();
  const meta = paymentStateMeta(state);
  return (
    <Badge variant={meta.variant} className={`cursor-default gap-1 ${className ?? ""}`}>
      {needsAttention && <AlertTriangle className="h-3 w-3" />}
      {paymentStateLabel(t, state)}
    </Badge>
  );
}

/** One "label: value" line inside the payment diagnostics tooltip. */
function DiagLine({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="opacity-70">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

/**
 * The Payment cell: a factual status badge. For flagged rows the badge carries a
 * hover tooltip with the persisted diagnostics an operator needs — amount due (or
 * "not finalized"), captured, CONFIRMED outstanding (only when the amount is known),
 * the authorization hold shown separately as diagnostic (never as money owed), any
 * decline / billing-error detail, and — when `showStripeLink` — a deep-link to the
 * Stripe payment intent (kept to the admin org report; hidden on the per-charger view).
 */
export function PaymentCell({
  r,
  showStripeLink = true,
}: {
  r: PaymentInfo;
  showStripeLink?: boolean;
}) {
  const t = useT();
  const badge = <PaymentBadge state={r.payment_state} needsAttention={r.payment_needs_attention} />;

  const hasDiagnostics =
    r.payment_needs_attention ||
    (showStripeLink && !!r.payment_intent_id) ||
    !!r.payment_last_decline_code ||
    !!r.billing_error ||
    !!r.resolved_billing_error ||
    r.payment_state === "unknown";

  if (!hasDiagnostics) {
    return <span title={r.payment_status ?? ""}>{badge}</span>;
  }

  const reasonLabel = r.attention_reason ? attentionReasonLabel(t, r.attention_reason) : null;
  const declineAt = viewerInstant(r.payment_last_decline_at);

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent className="max-w-[21rem] flex-col items-stretch gap-1 py-2 text-left">
        <div className="font-semibold">{paymentStateLabel(t, r.payment_state)}</div>
        {reasonLabel && <div className="mb-0.5 opacity-80">{reasonLabel}</div>}
        <DiagLine label={t("billing.payment_diag.status")} value={r.payment_status || "—"} />
        <DiagLine
          label={t("billing.payment_diag.amount_due")}
          value={r.amount_due != null ? money(r.amount_due) : t("billing.payment_diag.amount_due_not_finalized")}
        />
        <DiagLine label={t("billing.amount.captured")} value={money(r.captured_payment)} />
        <DiagLine
          label={t("billing.payment_diag.outstanding")}
          value={
            r.outstanding_known
              ? money(r.outstanding_amount)
              : t("billing.payment_diag.outstanding_unknown")
          }
        />
        {/* The authorization hold is a temporary Stripe pre-auth, NOT an amount owed. */}
        {r.authorized_hold_amount != null && (
          <DiagLine label={t("billing.payment_diag.auth_hold")} value={money(r.authorized_hold_amount)} />
        )}
        {r.billing_error && <DiagLine label={t("billing.payment_diag.billing_error")} value={r.billing_error} />}
        {r.resolved_billing_error && (
          <DiagLine label={t("billing.payment_diag.resolved_billing_error")} value={r.resolved_billing_error} />
        )}
        {r.payment_last_decline_code && (
          <DiagLine
            label={t("billing.payment_diag.last_decline")}
            value={
              declineAt
                ? t("billing.payment_diag.last_decline_at", { code: r.payment_last_decline_code, time: declineAt })
                : r.payment_last_decline_code
            }
          />
        )}
        {(r.payment_hard_decline_count ?? 0) > 0 && (
          <DiagLine label={t("billing.payment_diag.hard_declines")} value={r.payment_hard_decline_count} />
        )}
        {showStripeLink &&
          (r.payment_intent_id ? (
            <a
              href={stripePaymentUrl(r.payment_intent_id)}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 underline underline-offset-2 hover:opacity-80"
            >
              <LinkForward className="h-3 w-3" />
              {t("billing.payment_diag.open_in_stripe")}
            </a>
          ) : (r.payment_hard_decline_count ?? 0) > 0 ? (
            // record_hard_decline clears the intent; the next attempt creates a new one.
            <div className="mt-1 opacity-70">{t("billing.payment_diag.declined_intent_cleared")}</div>
          ) : (
            <div className="mt-1 opacity-70">{t("billing.payment_diag.no_payment_intent")}</div>
          ))}
      </TooltipContent>
    </Tooltip>
  );
}
